import React, { useState, useEffect, useMemo } from 'react';
import { View, Text, Modal } from 'react-native';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../../store';
import AdminModal from '../../components/AdminModal';
import AdminButton from '../../components/AdminButton';
import AdminInfoBanner from '../../components/AdminInfoBanner';
import { AdminFormField, AdminTextInput } from '../../components/AdminFormField';
import LocationFormSelect from './LocationFormSelect';
import LocationUserSearchSelect from './LocationUserSearchSelect';
import { modalStyles, dialogStyles } from './locationModalStyles';
import { Location, LocationFormValues, LOCATION_FACILITY_TYPE_OPTIONS } from './locationTypes';
import { locationApi } from './locationApi';
import FontFamily from '../../../../constants/fonts';

export interface EditLocationModalProps {
  visible: boolean;
  location: Location | null;
  employees?: any[];
  onClose: () => void;
  onSave: (loc: Location) => void;
  onSuccess?: (msg: string) => void;
}

interface EditErrors {
  name?: string;
  facilityType?: string;
  type?: string; org?: string; tpl?: string; size?: string;
  region?: string; market?: string;
}

function validateEditForm(f: Partial<LocationFormValues>): EditErrors {
  const e: EditErrors = {};
  if (!f.name?.trim())   e.name   = 'Location Name is required.';
  if (f.facilityType && !LOCATION_FACILITY_TYPE_OPTIONS.includes(f.facilityType)) {
    e.facilityType = 'Invalid Location Type selected.';
  }
  if (!f.type)          e.type   = 'Department is required.';
  if (!f.org)           e.org    = 'Organization Type is required.';
  if (!f.tpl)           e.tpl    = '3PL Status is required.';
  if (!f.size)          e.size   = 'Location Size is required.';
  if (!f.region?.trim()) e.region = 'Region is required.';
  if (!f.market?.trim()) e.market = 'Market is required.';
  return e;
}

const EditLocationModal: React.FC<EditLocationModalProps> = ({
  visible, location, employees, onClose, onSave, onSuccess,
}) => {
  const [form, setForm] = useState<LocationFormValues>({
    id: '',
    sap: '',
    name: '',
    status: '',
    facilityType: '',
    type: '',
    org: '',
    tpl: '',
    size: '',
    ownership: '',
    yearBuilt: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    latitude: '',
    longitude: '',
    region: '',
    market: '',
    marketLead: '',
    regionLead: '',
    coe: '',
    coe2: '',
    executionSystem: '',
    warehouseSystem: '',
    labor: '',
    union: '',
    supplier: '',
    totalSqft: '',
    operationalSqft: '',
    pickedVolume: '',
    docks: '',
    docksEng: '',
    docksNoEng: '',
    driveIn: '',
    bottleLines: '',
    canLines: '',
    build1: '',
    build2: '',
    bulkSite: '',
  });
  const [initialForm, setInitialForm] = useState<LocationFormValues | null>(null);
  const [errors, setErrors] = useState<EditErrors>({});
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showReactivatePrompt, setShowReactivatePrompt] = useState(false);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [regions, setRegions] = useState<string[]>([]);
  const [markets, setMarkets] = useState<string[]>([]);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [loadingMarkets, setLoadingMarkets] = useState(false);
  const [currentLocationId, setCurrentLocationId] = useState<string | null>(null);

  const fieldUsers = useSelector((state: RootState) => state.appData?.FieldUsers ?? []);
  const coeUsers   = useSelector((state: RootState) => state.appData?.COEUsers   ?? []);
  const allUsers   = useMemo(() => [...fieldUsers, ...coeUsers], [fieldUsers, coeUsers]);
  const marketLeadUsers  = useMemo(() => allUsers.filter((u: any) => Number(u.RoleID) === 3), [allUsers]);
  const coeResourceUsers = useMemo(() => allUsers.filter((u: any) => Number(u.RoleID) === 2), [allUsers]);

  const isDirty = useMemo(() => {
    if (!initialForm) return false;
    return (Object.keys(form) as (keyof LocationFormValues)[]).some(k => form[k] !== initialForm[k]);
  }, [form, initialForm]);

  const isReactivating = form.status === 'Active' && initialForm?.status === 'Disabled';
  const isDisabling = form.status === 'Disabled' && initialForm?.status === 'Active';
  const empCount = employees != null ? employees.length : (location?.empCount ?? 0);

  // Fetch regions when modal opens
  useEffect(() => {
    if (!visible) return;
    setLoadingRegions(true);
    locationApi.getRegions()
      .then(setRegions)
      .catch(() => setRegions([]))
      .finally(() => setLoadingRegions(false));
  }, [visible]);

  // Fetch markets whenever region changes
  useEffect(() => {
    if (!form.region) { setMarkets([]); return; }
    setLoadingMarkets(true);
    locationApi.getMarketsByRegion(form.region)
      .then(setMarkets)
      .catch(() => setMarkets([]))
      .finally(() => setLoadingMarkets(false));
  }, [form.region]);

  useEffect(() => {
    if (location && visible && location.id !== currentLocationId) {
      const f: LocationFormValues = {
        id: location.id ?? '',
        sap: location.sap ?? '',
        name: location.name ?? '',
        status: location.status ?? '',
        facilityType: location.facilityType ?? '',
        type: location.type ?? '',
        org: location.org ?? '',
        tpl: location.tpl ?? '',
        size: location.size ?? '',
        ownership: location.ownership ?? '',
        yearBuilt: location.yearBuilt ?? '',
        address: location.address ?? '',
        city: location.city ?? '',
        state: location.state ?? '',
        zip: location.zip ?? '',
        latitude: location.latitude ?? '',
        longitude: location.longitude ?? '',
        region: location.region ?? '',
        market: location.market ?? '',
        marketLead: location.marketLead ?? '',
        regionLead: location.regionLead ?? '',
        coe: location.coe ?? '',
        coe2: location.coe2 ?? '',
        executionSystem: location.executionSystem ?? '',
        warehouseSystem: location.warehouseSystem ?? '',
        labor: location.labor ?? '',
        union: location.union ?? '',
        supplier: location.supplier ?? '',
        totalSqft: location.totalSqft ?? '',
        operationalSqft: location.operationalSqft ?? '',
        pickedVolume: location.pickedVolume ?? '',
        docks: location.docks ?? '',
        docksEng: location.docksEng ?? '',
        docksNoEng: location.docksNoEng ?? '',
        driveIn: location.driveIn ?? '',
        bottleLines: location.bottleLines ?? '',
        canLines: location.canLines ?? '',
        build1: location.build1 ?? '',
        build2: location.build2 ?? '',
        bulkSite: location.bulkSite ?? '',
      };
      setForm(f);
      setInitialForm(f);
      setCurrentLocationId(location.id);
    }
    setApiError(null);
    setErrors({});
    setSubmitted(false);
    setShowCancelConfirm(false);
    setShowReactivatePrompt(false);
    setShowDisableConfirm(false);
  }, [location?.id, visible]);

  const set = (field: keyof LocationFormValues) => (val: string) => {
    const next = { ...form, [field]: val };
    // Clear market when region changes so stale combination can't be saved
    if (field === 'region') next.market = '';
    setForm(next);
    if (submitted) setErrors(validateEditForm(next));
  };

  const handleClose = () => {
    if (isDirty) {
      setShowCancelConfirm(true);
    } else {
      onClose();
    }
  };

  const doSave = async () => {
    if (!location || !form.id) return;
    setSaving(true);
    setApiError(null);
    try {
      const updated = await locationApi.updateFull(location.id, form as LocationFormValues);
      onSave({ ...updated, ...form });
      onSuccess?.(`Location '${form.name}' updated successfully`);
      onClose();
    } catch (e: any) {
      setApiError(e.message ?? 'Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!location) return;
    setSubmitted(true);
    const errs = validateEditForm(form);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    if (isReactivating) {
      setShowReactivatePrompt(true);
      return;
    }
    if (isDisabling) {
      setShowDisableConfirm(true);
      return;
    }
    await doSave();
  };

  const handleCancelDisable = () => {
    setShowDisableConfirm(false);
    if (initialForm) {
      setForm(prev => ({ ...prev, status: initialForm.status }));
    }
  };

  return (
    <>
      <AdminModal
        visible={visible}
        title={location ? `Edit Location: ${location.name}` : 'Edit Location'}
        onClose={handleClose}
        footer={
          <>
            <AdminButton label="Cancel" variant="secondary" onPress={handleClose} />
            <AdminButton label={saving ? 'Saving…' : 'Save Changes'} variant="primary" onPress={handleSave} />
          </>
        }
      >
        {apiError && <AdminInfoBanner message={apiError} />}
        <AdminInfoBanner message="Location ID and SAP ID cannot be changed after creation." />

        <Text style={modalStyles.section}>Primary Information</Text>
        <View style={modalStyles.row}>
          <View style={modalStyles.half}>
            <AdminFormField label="Location ID">
              <AdminTextInput value={location?.id} readOnly />
            </AdminFormField>
          </View>
          <View style={modalStyles.half}>
            <AdminFormField label="Location SAP ID">
              <AdminTextInput value={location?.sap} readOnly />
            </AdminFormField>
          </View>
        </View>
        <AdminFormField label="Location Name" required error={errors.name}>
          <AdminTextInput value={form.name} onChangeText={set('name')} maxLength={100} hasError={!!errors.name} />
        </AdminFormField>

        <Text style={modalStyles.section}>Facility Classification</Text>
        <View style={modalStyles.row}>
          <View style={modalStyles.half}>
            <AdminFormField label="Location Type" error={errors.facilityType}>
              <LocationFormSelect
                placeholder="Select location type…"
                options={LOCATION_FACILITY_TYPE_OPTIONS}
                value={form.facilityType ?? ''}
                onChange={set('facilityType')}
                hasError={!!errors.facilityType}
              />
            </AdminFormField>
          </View>
          <View style={modalStyles.half}>
            <AdminFormField label="Department" required error={errors.type}>
              <LocationFormSelect options={['Distribution', 'Production', 'Manufacturing']} value={form.type ?? ''} onChange={set('type')} hasError={!!errors.type} />
            </AdminFormField>
          </View>
        </View>
        <View style={modalStyles.row}>
          <View style={modalStyles.half}>
            <AdminFormField label="Organization Type" required error={errors.org}>
              <LocationFormSelect options={['PBNA', 'FLNA', '1NA']} value={form.org ?? ''} onChange={set('org')} hasError={!!errors.org} />
            </AdminFormField>
          </View>
          <View style={modalStyles.half}>
            <AdminFormField label="3PL Status" required error={errors.tpl}>
              <LocationFormSelect options={['Yes', 'No']} value={form.tpl ?? ''} onChange={set('tpl')} hasError={!!errors.tpl} />
            </AdminFormField>
          </View>
        </View>
        <View style={modalStyles.row}>
          <View style={modalStyles.half}>
            <AdminFormField label="Location Size" required error={errors.size}>
              <LocationFormSelect options={['Small', 'Medium', 'Large']} value={form.size ?? ''} onChange={set('size')} hasError={!!errors.size} />
            </AdminFormField>
          </View>
        </View>

        <Text style={modalStyles.section}>Geographic Information</Text>
        <View style={modalStyles.row}>
          <View style={modalStyles.half}>
            <AdminFormField label="Region" required error={errors.region}>
              <LocationFormSelect
                placeholder={loadingRegions ? 'Loading…' : 'Select region…'}
                options={regions}
                value={form.region ?? ''}
                onChange={set('region')}
                hasError={!!errors.region}
              />
            </AdminFormField>
          </View>
          <View style={modalStyles.half}>
            <AdminFormField label="Market" required error={errors.market}>
              <LocationFormSelect
                placeholder={!form.region ? 'Select region first…' : loadingMarkets ? 'Loading…' : 'Select market…'}
                options={markets}
                value={form.market ?? ''}
                onChange={set('market')}
                hasError={!!errors.market}
              />
            </AdminFormField>
          </View>
        </View>

        <Text style={modalStyles.section}>Resource Assignment</Text>
        <View style={modalStyles.row}>
          <View style={modalStyles.half}>
            <AdminFormField label="Market Lead" labelSuffix="Optional">
              <LocationUserSearchSelect
                users={marketLeadUsers}
                value={form.marketLead ?? ''}
                onChange={set('marketLead')}
                placeholder="Search Navigator users…"
              />
            </AdminFormField>
          </View>
          <View style={modalStyles.half}>
            <AdminFormField label="Capability Resource" labelSuffix="Optional">
              <LocationUserSearchSelect
                users={coeResourceUsers}
                value={form.coe ?? ''}
                onChange={set('coe')}
                placeholder="Search Navigator users…"
              />
            </AdminFormField>
          </View>
        </View>
        <AdminFormField label="E2E Resource" labelSuffix="Optional">
          <LocationUserSearchSelect
            users={coeResourceUsers}
            value={form.coe2 ?? ''}
            onChange={set('coe2')}
            placeholder="Search Navigator users…"
          />
        </AdminFormField>

        <Text style={modalStyles.section}>Status</Text>
        <AdminFormField label="Location Status">
          <LocationFormSelect options={['Active', 'Disabled']} value={form.status ?? ''} onChange={set('status')} />
        </AdminFormField>
        {isReactivating && (
          <AdminInfoBanner message="You are re-enabling this location. Please review and update any blank or outdated fields before saving." />
        )}
        {form.status === 'Disabled' && initialForm?.status !== 'Disabled' && empCount > 0 && (
          <AdminInfoBanner
            variant="warn"
            message={
              <Text>
                <Text style={{ fontFamily: FontFamily.bold }}>{empCount} active {empCount === 1 ? 'employee' : 'employees'}</Text>
                {' '}are currently assigned to this location. Disabling will restrict their access.
              </Text>
            }
          />
        )}

        <Text style={modalStyles.section}>Systems</Text>
        <AdminFormField label="Execution System">
          <LocationFormSelect
            placeholder="Select execution system…"
            options={['EWM', 'Voice Pick', 'Vision Pick (WES)', 'WMS']}
            value={form.executionSystem ?? ''}
            onChange={set('executionSystem')}
          />
        </AdminFormField>
      </AdminModal>

      {/* Cancel confirmation dialog */}
      <Modal visible={showCancelConfirm} transparent animationType="fade" onRequestClose={() => setShowCancelConfirm(false)}>
        <View style={dialogStyles.overlay}>
          <View style={dialogStyles.box}>
            <Text style={dialogStyles.title}>Discard Changes?</Text>
            <Text style={dialogStyles.msg}>You have unsaved changes. Are you sure you want to discard them?</Text>
            <View style={dialogStyles.footer}>
              <AdminButton label="Keep Editing" variant="secondary" onPress={() => setShowCancelConfirm(false)} />
              <AdminButton label="Discard" variant="primary" onPress={() => { setShowCancelConfirm(false); onClose(); }} />
            </View>
          </View>
        </View>
      </Modal>

      {/* Reactivation review prompt */}
      <Modal visible={showReactivatePrompt} transparent animationType="fade" onRequestClose={() => setShowReactivatePrompt(false)}>
        <View style={dialogStyles.overlay}>
          <View style={dialogStyles.box}>
            <Text style={dialogStyles.title}>Re-enabling Location</Text>
            <Text style={dialogStyles.msg}>
              You are re-enabling this location. Please review the form and update any fields that are blank or outdated before saving. Would you like to keep editing, or save now?
            </Text>
            <View style={dialogStyles.footer}>
              <AdminButton label="Keep Editing" variant="secondary" onPress={() => setShowReactivatePrompt(false)} />
              <AdminButton label={saving ? 'Saving…' : 'Save Now'} variant="primary" onPress={doSave} />
            </View>
          </View>
        </View>
      </Modal>

      {/* Disable confirmation prompt */}
      <Modal visible={showDisableConfirm} transparent animationType="fade" onRequestClose={() => setShowDisableConfirm(false)}>
        <View style={dialogStyles.overlay}>
          <View style={dialogStyles.box}>
            <Text style={dialogStyles.title}>Disable Location?</Text>
            <Text style={dialogStyles.msg}>
              {empCount > 0
                ? `${empCount} active ${empCount === 1 ? 'employee' : 'employees'} are currently assigned to this location. Disabling will restrict their access. Are you sure you want to proceed?`
                : 'Are you sure you want to disable this location?'}
            </Text>
            <View style={dialogStyles.footer}>
              <AdminButton label="Cancel" variant="secondary" onPress={handleCancelDisable} />
              <AdminButton label={saving ? 'Disabling…' : 'Disable'} variant="primary" onPress={doSave} />
            </View>
          </View>
        </View>
      </Modal>
    </>
  );
};

export default EditLocationModal;
